// ── GBP API client — typed wrappers around /api/gbp/* ─────────────────────────
// All calls go to same-origin /api/gbp/* which are Vercel serverless functions
// in production and proxied to Express (port 3001) in local dev.

export type Tone = 'professional' | 'friendly' | 'premium' | 'casual';
export type ReplyStatus = 'none' | 'draft' | 'published';

export interface GBPAccount {
  id:                   string;
  location_key:         string;
  display_name:         string;
  connected:            boolean;
  google_email:         string | null;
  google_location_name: string | null;
  google_location_title:string | null;
  google_location_mapped_at: string | null;
  last_synced_at:       string | null;
  connect_error:        string | null;
  totalReviews:         number;
  unanswered:           number;
  lowStarReviews:       number;
  avgRating:            number | null;
}

export interface GBPLocationOption {
  account_name:         string;
  account_display_name: string;
  google_email:         string | null;
  location_name:        string;
  title:                string;
  address:              string | null;
  locality:             string | null;
  administrative_area:  string | null;
  postal_code:          string | null;
}

export interface GBPReview {
  id:                  string;
  review_name:         string;
  reviewer_name:       string;
  reviewer_photo_url:  string | null;
  star_rating:         number;
  review_text:         string | null;
  review_date:         string;
  reply_text:          string | null;
  reply_date:          string | null;
  reply_draft:         string | null;
  reply_status:        ReplyStatus;
  synced_at:           string;
  gbp_accounts:        { location_key: string; display_name: string };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  const text = await res.text();
  let data: T;
  try { data = JSON.parse(text) as T; }
  catch { throw new Error(`HTTP ${res.status} — non-JSON: ${text.slice(0, 200)}`); }
  if (!res.ok) throw new Error((data as { error?: string }).error || `HTTP ${res.status}`);
  return data;
}

// ── Accounts ──────────────────────────────────────────────────────────────────

export async function getAccounts(): Promise<GBPAccount[]> {
  const d = await apiFetch<{ accounts: GBPAccount[] }>('/api/gbp/accounts');
  return d.accounts;
}

export async function syncAllAccounts(): Promise<Record<string, unknown>> {
  const d = await apiFetch<{ ok: boolean; results: Record<string, unknown> }>(
    '/api/gbp/accounts',
    { method: 'POST', body: JSON.stringify({ action: 'sync' }) },
  );
  return d.results;
}

export async function listAccessibleLocations(locationKey: string): Promise<GBPLocationOption[]> {
  const d = await apiFetch<{ locations: GBPLocationOption[] }>('/api/gbp/accounts', {
    method: 'POST',
    body: JSON.stringify({ action: 'list_locations', location_key: locationKey }),
  });
  return d.locations;
}

export async function mapGoogleLocation(locationKey: string, googleLocationName: string): Promise<GBPLocationOption> {
  const d = await apiFetch<{ ok: boolean; mapped: GBPLocationOption }>('/api/gbp/accounts', {
    method: 'POST',
    body: JSON.stringify({
      action: 'map_location',
      location_key: locationKey,
      google_location_name: googleLocationName,
    }),
  });
  return d.mapped;
}

export async function getAuthUrl(locationKey: string): Promise<string> {
  const d = await apiFetch<{ url: string }>(`/api/gbp/auth-url?location_key=${encodeURIComponent(locationKey)}`);
  return d.url;
}

// ── Reviews ───────────────────────────────────────────────────────────────────

export interface ReviewFilters {
  location_key?: string;
  reply_status?: ReplyStatus | '';
  min_stars?: number;
  max_stars?: number;
  sort?: 'newest' | 'oldest';
  page?: number;
  per_page?: number;
}

export async function getReviews(filters: ReviewFilters = {}): Promise<{ reviews: GBPReview[]; total: number }> {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') params.set(k, String(v)); });
  return apiFetch<{ reviews: GBPReview[]; total: number }>(`/api/gbp/reviews?${params}`);
}

export async function saveDraft(reviewId: string, draftText: string) {
  return apiFetch<{ ok: boolean }>('/api/gbp/reviews', {
    method: 'POST',
    body: JSON.stringify({ action: 'draft', review_id: reviewId, draft_text: draftText }),
  });
}

export async function syncReviews(locationKey: string): Promise<number> {
  const d = await apiFetch<{ ok: boolean; synced: number }>('/api/gbp/reviews', {
    method: 'POST',
    body: JSON.stringify({ action: 'sync', location_key: locationKey }),
  });
  return d.synced;
}

// ── AI Reply ──────────────────────────────────────────────────────────────────

export async function generateAiReply(params: {
  reviewer_name: string;
  star_rating: number;
  review_text?: string;
  tone: Tone;
  business_name: string;
}): Promise<string> {
  const d = await apiFetch<{ reply: string }>('/api/gbp/ai-reply', {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return d.reply;
}
